import { useRef, useState, useEffect, useCallback } from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Row, Col, Carousel } from 'antd';
import { useMediaQuery } from 'react-responsive';

import { IFlat } from '../../common/model/flat.model';
import { IOffice } from '../../common/model/office.model';
import { split } from '../../common/helpers';

import FlatCard from './flatCard';

type Props = {
  flats: IFlat[];
  offices: IOffice[];
  title: string;
  centerMode?: boolean;
  arrows?: boolean;
  newTab?: boolean;
  className?: string;
  theme: DefaultTheme;
};

const Title = styled.h2`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
  background-color: #e0e0e0;
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
`;

const Placeholder = styled.div`
  height: 600px;
`;

const RelativeDiv = styled.div<{ $centerMode: boolean }>`
  position: relative;
  width: ${(props) => (props.$centerMode ? 'unset' : '100%')};
  margin-left: ${(props) =>
    props.$centerMode ? props.theme.grid.getGridColumns(1, 1) : 'unset'};
  margin-right: ${(props) =>
    props.$centerMode ? props.theme.grid.getGridColumns(1, 1) : 'unset'};

  @media ${(props) => props.theme.breakpoints.lgd} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const Arrow = styled.div<{ $left?: boolean }>`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.$left ? '4%' : 'inherit')};
  right: ${(props) => (!props.$left ? '4%' : 'inherit')};
  transform: translateY(calc(-50% - 28px));
  width: 40px;
  height: 40px;
  background-image: ${(props) =>
    props.$left
      ? 'url(/images/prev_black.svg)'
      : 'url(/images/next_black.svg)'};
  cursor: pointer;
  z-index: 1;
`;

const FlatPage = styled.div<{ $centerMode: boolean }>`
  padding-bottom: 32px;
  padding-left: ${(props) =>
    props.theme.grid.getGridColumns(props.$centerMode ? 1 : 2, 1)};
  padding-right: ${(props) =>
    props.theme.grid.getGridColumns(props.$centerMode ? 1 : 2, 1)};
`;

const FlatsDisplay: React.FC<Props> = ({
  flats,
  offices,
  title,
  centerMode = false,
  arrows = false,
  newTab = false,
  className,
  theme,
}) => {
  const carouselRef = useRef(null);

  const isXxl = useMediaQuery({ query: theme.breakpoints.xxl });
  const isXl = useMediaQuery({ query: theme.breakpoints.xl });
  const isLg = useMediaQuery({ query: theme.breakpoints.lg });
  const isMd = useMediaQuery({ query: theme.breakpoints.md });
  const isSm = useMediaQuery({ query: theme.breakpoints.sm });
  const isXs = useMediaQuery({ query: theme.breakpoints.xs });

  const [isMounted, setIsMounted] = useState(false);

  const getFlatOffice = useCallback(
    (flat: IFlat) => offices.find((office) => office.id === flat.officeId),
    [offices]
  );

  const isCenterMode = useCallback(() => centerMode || !(isXl || isXxl), [
    centerMode,
    isXl,
    isXxl,
  ]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  let auxFlats = flats;
  let flatsPerPage: number;
  let centerPadding: string;
  if (!isCenterMode()) {
    if (isXxl) {
      flatsPerPage = 8;
    } else if (isXl) {
      flatsPerPage = 6;
    }

    if (flats.length < flatsPerPage) {
      auxFlats = flats.slice(
        0,
        Math.max(
          flatsPerPage / 2,
          Math.floor(flats.length / (flatsPerPage / 2))
        )
      );
    }
  } else {
    flatsPerPage = 1;
    auxFlats = flats.slice(0, 8);
    if (isXxl || isXl) {
      centerPadding = '15%';
    } else if (isLg) {
      centerPadding = '25%';
    } else if (isMd) {
      centerPadding = '20%';
    } else if (isSm) {
      centerPadding = '15%';
    } else if (isXs) {
      centerPadding = '10%';
    }
  }

  const flatPages = split(auxFlats, flatsPerPage);

  const prev = () => {
    if (carouselRef.current) {
      carouselRef.current.prev();
    }
  };

  const next = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };

  return (
    <div className={className}>
      <Title
        key={title.substring(0, 10) + new Date().getTime()}
        as="h2"
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <Divider />
      {isMounted ? (
        <RelativeDiv as="div" $centerMode={centerMode}>
          {arrows && (isXl || isXxl) && !isCenterMode() && (
            <Arrow as="div" $left onClick={prev} />
          )}
          <Carousel
            dots={false}
            lazyLoad="ondemand"
            centerMode={isCenterMode()}
            centerPadding={centerPadding}
            draggable={!(isXl || isXxl) || isCenterMode()}
            ref={carouselRef}
          >
            {flatPages.map((flatPage, i) => (
              <div key={`page_${i}`}>
                <FlatPage as="div" $centerMode={isCenterMode()}>
                  <Row gutter={[32, 32]} justify="center">
                    {flatPage.map((flat) => (
                      <Col
                        key={flat.id}
                        xs={24}
                        xl={isCenterMode() ? 24 : 8}
                        xxl={isCenterMode() ? 24 : 6}
                      >
                        <FlatCard
                          flat={flat}
                          office={getFlatOffice(flat)}
                          forceVerticalMode={true}
                          useCarousel={isXl || isXxl}
                          newTab={newTab}
                        />
                      </Col>
                    ))}
                  </Row>
                </FlatPage>
              </div>
            ))}
          </Carousel>
          {arrows && (isXl || isXxl) && !isCenterMode() && (
            <Arrow as="div" onClick={next} />
          )}
        </RelativeDiv>
      ) : (
        <Placeholder />
      )}
    </div>
  );
};

export default withTheme<typeof FlatsDisplay>(styled(FlatsDisplay)`
  padding-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 2rem;
  }
`);
